import React from 'react'
import { List, ListItem, Divider, MarginBox, H2 } from '@fs/zion-ui'
import { useLocation } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import ContentWrapper from '../ContentWrapper'

const BASE_PATH = '/editor-handbook'

type NavSubItem = {
  id: string
  primaryText: string
  to: string
}

type NavItem = {
  id: string
  primaryText: string
  to: string
  showDivider?: boolean
  isHeader?: boolean
  subItems?: NavSubItem[]
}

const buildPath = (path = ''): string => {
  if (!path) return `${BASE_PATH}/`
  return `${BASE_PATH}/${path}/`
}

const createNavItem = (id: string, primaryText: string, path: string, options: Partial<NavItem> = {}): NavItem => ({
  id,
  primaryText,
  to: buildPath(path),
  ...options,
})

const createSubItem = (id: string, primaryText: string, category: string): NavSubItem => ({
  id,
  primaryText,
  to: buildPath(`${category}/${id}`),
})

export default function EditorHandbookNav(): JSX.Element {
  const location = useLocation()
  const [t] = useTranslation()

  const isActive = (item: NavItem): boolean =>
    item.to === buildPath('') ? location.pathname === buildPath('') : location.pathname.startsWith(item.to)

  const NAV_ITEMS: NavItem[] = [
    createNavItem('landing', t('editor-handbook.nav.landing.label', 'Editor Handbook'), ''),
    createNavItem(
      'mission-and-purpose',
      t('editor-handbook.nav.mission-and-purpose.label', 'Mission & Purpose'),
      'mission-and-purpose'
    ),
    createNavItem('training', t('editor-handbook.nav.training.label', 'Editor Training'), 'training'),
    createNavItem('faqs', t('editor-handbook.nav.faqs.label', 'FAQs'), 'faqs'),
    createNavItem('general-info', t('editor-handbook.nav.general-info.label', 'General Information'), '', {
      showDivider: true,
      isHeader: true,
      subItems: [
        createSubItem(
          'historical-periods',
          t('editor-handbook.nav.historical-periods.label', 'Historical Periods'),
          'historical'
        ),
        createSubItem(
          'creating-new-entry',
          t('editor-handbook.nav.creating-new-entry.label', 'Creating a Place'),
          'general-info'
        ),
        createSubItem(
          'deleting-entries',
          t('editor-handbook.nav.deleting-entries.label', 'Deleting a Place'),
          'general-info'
        ),
      ],
    }),
    createNavItem('primary-fields', t('editor-handbook.nav.primary-fields.label', 'Primary Fields'), '', {
      showDivider: true,
      isHeader: true,
      subItems: [
        createSubItem('parents', t('editor-handbook.nav.parents.label', 'Parent Places'), 'place-fields'),
        createSubItem('place-types', t('editor-handbook.nav.place-types.label', 'Place Type'), 'place-fields'),
        createSubItem(
          'native-language-years-location',
          t('editor-handbook.nav.native-language-years-location.label', 'Native Language, Years, Location'),
          'other-fields'
        ),
        createSubItem(
          'certification-status',
          t('editor-handbook.nav.certification-status.label', 'Certification Status'),
          'place-fields'
        ),
      ],
    }),
    createNavItem('additional-fields', t('editor-handbook.nav.additional-fields.label', 'Additional Fields'), '', {
      showDivider: true,
      isHeader: true,
      subItems: [
        createSubItem('notes', t('editor-handbook.nav.notes.label', 'Notes'), 'other-fields'),
        createSubItem(
          'historical-information',
          t('editor-handbook.nav.historical-information.label', 'Historical Information'),
          'historical'
        ),
        createSubItem(
          'research-links-citations',
          t('editor-handbook.nav.research-links-citations.label', 'Research Links & Citations'),
          'other-fields'
        ),
        createSubItem(
          'place-names',
          t('editor-handbook.nav.place-names.label', 'Alternate and Display Names'),
          'place-fields'
        ),
        createSubItem(
          'related-places',
          t('editor-handbook.nav.related-places.label', 'Related Places'),
          'place-fields'
        ),
      ],
    }),
  ]

  return (
    <ContentWrapper>
      <MarginBox marginX="nano" marginY="sm">
        <List>
          {NAV_ITEMS.map((item) => (
            <React.Fragment key={item.id}>
              {item.showDivider && <Divider marginY="sm" />}

              {item.isHeader ? (
                <MarginBox marginY="xs" marginX="nano">
                  <H2 size="H6">{item.primaryText}</H2>
                </MarginBox>
              ) : (
                <ListItem dense primaryText={item.primaryText} to={item.to} selected={isActive(item)} />
              )}

              {item.subItems && (
                <div style={{ paddingInlineStart: '16px' }}>
                  {item.subItems.map((subItem) => (
                    <ListItem
                      key={subItem.id}
                      dense
                      primaryText={subItem.primaryText}
                      to={subItem.to}
                      selected={location.pathname.startsWith(subItem.to)}
                    />
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </List>
      </MarginBox>
    </ContentWrapper>
  )
}
